import { Dispatch, ReactNode, SetStateAction } from 'react'
import moment from 'moment'
import { DialogWrapper } from 'components/dialogWrapper'
import { resolveHtmlMediaSrc } from 'components/articleEditor/helpers'
import 'components/articleEditor/ArticleContentEditor.css'
import { Article, unwrapResponse, useGetArticleDetails } from 'hooks/article'
import ArticleStatusBadge from './ArticleStatusBadge'

interface ViewArticleDialogProps {
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    articleData: { id?: number | string } | null
    canPublish?: boolean
    publishing?: boolean
    publishError?: string | null
    onPublish?: (article: Article) => void
}

const formatDate = (value?: string | null) => (value ? moment(value).format('DD MMM YYYY') : '—')

const ViewArticleDialog = ({ isOpen, setIsOpen, articleData, canPublish, publishing, publishError, onPublish }: ViewArticleDialogProps) => {
    const { data, isLoading, isError } = useGetArticleDetails(isOpen ? articleData?.id : null)
    if (!isOpen) return null

    const article = unwrapResponse(data) as Article | undefined

    const details: { label: string; value: ReactNode }[] = article
        ? [
            { label: 'Status', value: <ArticleStatusBadge status={article.status} /> },
            { label: 'Author', value: article.author || '—' },
            {
                label: 'Custom URL',
                value: article.slug ? (
                    <span className="break-all font-normal">/{article.slug}</span>
                ) : (
                    '—'
                ),
            },
            { label: 'Version', value: article.version },
            { label: 'Created', value: formatDate(article.created_at) },
            { label: 'Last updated', value: formatDate(article.updated_at) },
            { label: 'Published', value: formatDate(article.published_at) },
        ]
        : []

    return (
        <DialogWrapper
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title="Article Preview"
            panelClass="!max-w-3xl"
        >
            <div className="max-h-[75vh] space-y-4 overflow-y-auto overflow-x-hidden p-1">
                {isLoading && (
                    <p className="py-10 text-center text-sm text-gray-500">Loading article…</p>
                )}
                {(isError || (!isLoading && !article)) && (
                    <p className="py-10 text-center text-sm text-red-500">
                        This article could not be loaded.
                    </p>
                )}

                {article && (
                    <>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                {article.title}
                            </h3>
                            {article.short_description && (
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                    {article.short_description}
                                </p>
                            )}
                        </div>

                        <table className="w-full table-fixed border-collapse border border-gray-300 dark:border-slate-600">
                            <tbody>
                                {details.map(({ label, value }) => (
                                    <tr key={label}>
                                        <td className="w-36 border border-gray-300 px-4 py-2 align-top text-sm text-gray-500 dark:border-slate-600 dark:text-gray-300">
                                            {label}
                                        </td>
                                        <td className="min-w-0 break-words border border-gray-300 px-4 py-2 align-top text-sm font-medium dark:border-slate-600">
                                            {value}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* content_html is sanitised by the backend before it is stored */}
                        <div
                            className="article-body overflow-x-hidden rounded border border-gray-200 p-4 text-sm dark:border-slate-700"
                            dangerouslySetInnerHTML={{
                                __html: resolveHtmlMediaSrc(article.content_html),
                            }}
                        />
                    </>
                )}

                {publishError && (
                    <p className="text-sm text-red-600">{publishError}</p>
                )}
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        onClick={() => setIsOpen(false)}
                        className="rounded bg-gray-300 px-4 py-2 text-gray-800 dark:bg-slate-700 dark:text-slate-200"
                    >
                        Close
                    </button>
                    {canPublish && article?.status === 'approved' && (
                        <button
                            type="button"
                            disabled={publishing}
                            onClick={() => onPublish?.(article)}
                            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            {publishing ? 'Publishing...' : 'Publish'}
                        </button>
                    )}
                </div>
            </div>
        </DialogWrapper>
    )
}

export default ViewArticleDialog